import React, { useState, useEffect } from 'react'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import heatmap from 'highcharts/modules/heatmap'
import exporting from 'highcharts/modules/exporting'
import exportingOffline from 'highcharts/modules/offline-exporting'
import { getValue } from '../../../../Utility/SharedUtility'
import { DimensionNames } from '../../../../Constants/DimensionNames'
import { Badge } from '@mds/mds-reactjs-library'

// Initialize the heatmap and exporting modules
heatmap(Highcharts)
exporting(Highcharts)
exportingOffline(Highcharts)

// Custom templating helpers for axis labels
if (Highcharts.Templating && Highcharts.Templating.helpers) {
  Highcharts.Templating.helpers.substr = (s, from, length) =>
    (s != null ? String(s).substr(Number(from), Number(length)) : '')
  // Replace spaces with <br/> so labels wrap at word boundaries (use with useHTML: true)
  Highcharts.Templating.helpers.wrapOnSpace = (s) =>
    (s != null ? String(s).replace(/ /g, '<br/>') : '')
}

/** Default chart width; may grow when many columns would make cells narrower than `HEATMAP_MIN_COLUMN_SLOT_PX`. */
export const HEATMAP_CHART_WIDTH = 1100
export const HEATMAP_MARGIN_RIGHT = 28
/** Widen the chart when needed so each column is at least this wide (plot inner / category count). */
export const HEATMAP_MIN_COLUMN_SLOT_PX = 60
/** Pixels reserved between the y-axis label box and the plot edge. */
const Y_AXIS_LABEL_LEFT_PADDING_PX = 68
/** Fixed left margin (label width + padding); deliberately constant so layout doesn't reflow per selection. */
export const HEATMAP_MARGIN_LEFT = 200 + Y_AXIS_LABEL_LEFT_PADDING_PX
/**
 * Horizontal x-axis labels: wrap to this many lines, then ellipsis (Highcharts HTML + line-clamp).
 * Keeps headers readable without rotation; full string on native `title` if clamped.
 * @see https://www.highcharts.com/demo/highcharts/heatmap
 */
export const X_AXIS_LABEL_DISPLAY_LINES = 2
export const X_AXIS_LABEL_LINE_HEIGHT_EM = 1.35
export const X_AXIS_LABEL_FONT_PX = 13
/** More than this many x categories (incl. total columns): tilt labels + single-line truncation. */
export const X_AXIS_MANY_CATEGORIES_THRESHOLD = 10
/** −45° keeps vertical footprint smaller than −55/−70 while staying readable. */
export const X_AXIS_TILTED_LABEL_ROTATION_DEG = -45
/** Floor for tilted-label band under title; paired with `getTiltedXLabelsVerticalReservePx` (tighter than before to cut dead space). */
export const X_AXIS_TILTED_TOP_CLEARANCE_PX = 88
/** Top-axis label distance (side 0); moderate value keeps labels near the grid without overlapping row 0. */
export const X_AXIS_TILTED_LABEL_DISTANCE_PX = 12
/** Show start + end so “Fundamental …” columns differ; full string stays on `title`. */
const X_AXIS_TILTED_HEAD_CHARS = 15
const X_AXIS_TILTED_TAIL_CHARS = 13

/** Y-axis row labels (left). Fixed width sized so "Total Net Flows (Downside: 2025E - Downside: 2028E)"
 *  wraps into ~2–3 lines (≈51 chars / ~17 chars per line at 13px). Constant so layout is stable across selections. */
export const Y_AXIS_LABEL_WIDTH_PX = 150
const Y_AXIS_LABEL_FONT_PX = 13
const Y_AXIS_LABEL_LINE_HEIGHT_EM = 1.35
const Y_AXIS_LABEL_MAX_LINES = 5
/** Fixed uniform row height; no logic. Tall enough for "Total Net Flows (Downside: …)" wrapped to 2–3 lines. */
export const HEATMAP_ROW_HEIGHT_PX = 70

export function getPlotInnerWidthPx(chartWidthPx = HEATMAP_CHART_WIDTH, marginLeftPx = HEATMAP_MARGIN_LEFT) {
  return chartWidthPx - marginLeftPx - HEATMAP_MARGIN_RIGHT
}

export function getXAxisColumnSlotPx(
  numCategories,
  chartWidthPx = HEATMAP_CHART_WIDTH,
  marginLeftPx = HEATMAP_MARGIN_LEFT
) {
  return getPlotInnerWidthPx(chartWidthPx, marginLeftPx) / Math.max(1, numCategories)
}

/** Label box width tracks column slot; tilted caps avoid huge diagonal spill into the plot. */
export function getResponsiveXLabelWidth(
  numCategories,
  tilted = false,
  chartWidthPx = HEATMAP_CHART_WIDTH,
  marginLeftPx = HEATMAP_MARGIN_LEFT
) {
  const slotPx = getXAxisColumnSlotPx(numCategories, chartWidthPx, marginLeftPx)
  if (tilted) {
    const fraction = 1.08
    const minPx = 72
    const maxPx = 132
    return Math.round(Math.min(maxPx, Math.max(minPx, slotPx * fraction)))
  }
  const fraction = 0.96
  const minPx = 72
  const maxPx = 220
  return Math.round(Math.min(maxPx, Math.max(minPx, slotPx * fraction)))
}

/** Vertical band for tilted labels under the title block (not full diagonal — labels are centered on ticks). */
export function getTiltedXLabelsVerticalReservePx(labelBoxWidthPx) {
  const rad = (Math.abs(X_AXIS_TILTED_LABEL_ROTATION_DEG) * Math.PI) / 180
  const diagonal = labelBoxWidthPx * Math.sin(rad)
  const lineH = X_AXIS_LABEL_FONT_PX * X_AXIS_LABEL_LINE_HEIGHT_EM
  return Math.ceil(diagonal * 0.62 + lineH + X_AXIS_TILTED_LABEL_DISTANCE_PX + 20)
}

function shortenTiltedCategoryLabel(raw) {
  const s = String(raw ?? '').trim()
  const minKeep = X_AXIS_TILTED_HEAD_CHARS + X_AXIS_TILTED_TAIL_CHARS + 2
  if (s.length <= minKeep) return s
  return `${s.slice(0, X_AXIS_TILTED_HEAD_CHARS)}…${s.slice(-X_AXIS_TILTED_TAIL_CHARS)}`
}

// Separator between "Net Flows Total …" row/col and "AuM Total …" row/col
export const TOTAL_BLOCK_SEPARATOR_COLOR = '#000000'
// Outer frame around the heatmap plot (distinct from total block separators)
export const CHART_TABLE_OUTER_BORDER_COLOR = '#333333'

// Heatmap colour code: cell RGB and font color per range
const HEATMAP_COLORS = {
  belowZero: { color: 'rgb(229, 84, 108)', fontColor: '#ffffff' },
  zeroToTwo: { color: 'rgb(204, 255, 255)', fontColor: '#000000' },
  twoToFour: { color: 'rgb(0, 176, 240)', fontColor: '#000000' },
  fourToSix: { color: 'rgb(31, 64, 230)', fontColor: '#ffffff' },
  aboveSix: { color: 'rgb(0, 32, 96)', fontColor: '#ffffff' },
  na: { color: 'rgb(230, 230, 230)', fontColor: '#000000' },
}

function getHeatmapFontColor(value) {
  if (value == null || Number.isNaN(value)) return HEATMAP_COLORS.na.fontColor
  if (value < 0) return HEATMAP_COLORS.belowZero.fontColor
  if (value >= 0 && value <= 2) return HEATMAP_COLORS.zeroToTwo.fontColor
  if (value > 2 && value <= 4) return HEATMAP_COLORS.twoToFour.fontColor
  if (value > 4 && value <= 6) return HEATMAP_COLORS.fourToSix.fontColor
  return HEATMAP_COLORS.aboveSix.fontColor
}

function getHeatmapCellColor(value) {
  if (value == null || Number.isNaN(value)) return HEATMAP_COLORS.na.color
  if (value < 0) return HEATMAP_COLORS.belowZero.color
  if (value >= 0 && value <= 2) return HEATMAP_COLORS.zeroToTwo.color
  if (value > 2 && value <= 4)  return HEATMAP_COLORS.twoToFour.color
  if (value > 4 && value <= 6) return HEATMAP_COLORS.fourToSix.color
  return HEATMAP_COLORS.aboveSix.color
}

/** Plain-text for HTML body */
function escapeHtml(text) {
  if (text == null) return ''
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** Double-quoted attribute (e.g. title="…") */
function escapeHtmlAttr(text) {
  if (text == null) return ''
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
}

/**
 * X-axis labels: either multi-line clamp (≤ threshold columns) or single-line ellipsis (tilted dense mode).
 * Full text always in `title`.
 */
function buildXAxisCategoryLabelHtml(
  raw,
  widthPx,
  textAlignCss = 'center',
  maxLines = X_AXIS_LABEL_DISPLAY_LINES,
  singleLineEllipsis = false,
  /** Optional visible text; `raw` is still used for the `title` tooltip. */
  displayText = null
) {
  const safeTitle = escapeHtmlAttr(raw)
  const safeBody = escapeHtml(displayText != null ? displayText : raw)
  const lh = X_AXIS_LABEL_LINE_HEIGHT_EM
  const fs = X_AXIS_LABEL_FONT_PX

  if (singleLineEllipsis) {
    const style = [
      'box-sizing:border-box',
      `width:${widthPx}px`,
      `max-width:${widthPx}px`,
      `text-align:${textAlignCss}`,
      `line-height:${lh}em`,
      `font-size:${fs}px`,
      "font-family:'McKinsey Sans Regular'",
      'white-space:nowrap',
      'overflow:hidden',
      'text-overflow:ellipsis',
    ].join(';')
    return `<div title="${safeTitle}" style="${style}">${safeBody}</div>`
  }

  const n = maxLines
  const style = [
    'box-sizing:border-box',
    `width:${widthPx}px`,
    `max-width:${widthPx}px`,
    `text-align:${textAlignCss}`,
    `line-height:${lh}em`,
    `font-size:${fs}px`,
    "font-family:'McKinsey Sans Regular'",
    'white-space:normal',
    'overflow-wrap:break-word',
    'word-break:break-word',
    'display:-webkit-box',
    '-webkit-box-orient:vertical',
    `-webkit-line-clamp:${n}`,
    'overflow:hidden',
    'text-overflow:ellipsis',
  ].join(';')
  return `<div title="${safeTitle}" style="${style}">${safeBody}</div>`
}

/**
 * Highcharts calls `labels.formatter` as `formatter.call(axisLabelContext)` — `this` is the library context object
 * (e.g. `{ value, pos, … }`), not a React component instance. Only this thin adapter uses `this`; React code stays pure.
 */
export function createXAxisLabelsFormatter(widthPx, textAlignCss = 'center', useTiltedTruncation = false) {
  return function highchartsXAxisLabelFormatter() {
    const raw = this.value != null ? String(this.value) : ''
    const display = useTiltedTruncation ? shortenTiltedCategoryLabel(raw) : null
    return buildXAxisCategoryLabelHtml(
      raw,
      widthPx,
      textAlignCss,
      X_AXIS_LABEL_DISPLAY_LINES,
      useTiltedTruncation,
      display
    )
  }
}

function buildYAxisCategoryLabelHtml(raw, widthPx) {
  const safeTitle = escapeHtmlAttr(raw)
  const safeBody = escapeHtml(raw)
  const n = Y_AXIS_LABEL_MAX_LINES
  const lh = Y_AXIS_LABEL_LINE_HEIGHT_EM
  const fs = Y_AXIS_LABEL_FONT_PX
  const style = [
    'box-sizing:border-box',
    `width:${widthPx}px`,
    `max-width:${widthPx}px`,
    'text-align:left',
    `line-height:${lh}em`,
    `font-size:${fs}px`,
    "font-family:'McKinsey Sans Regular'",
    'white-space:normal',
    'overflow-wrap:break-word',
    'word-break:break-word',
    'display:-webkit-box',
    '-webkit-box-orient:vertical',
    `-webkit-line-clamp:${n}`,
    'overflow:hidden',
  ].join(';')
  return `<div title="${safeTitle}" style="${style}">${safeBody}</div>`
}

function formatYAxisCategoryLabel(categoryText, widthPx) {
  const raw = categoryText != null ? String(categoryText) : ''
  return buildYAxisCategoryLabelHtml(raw, widthPx)
}

export function createYAxisLabelsFormatter(widthPx) {
  return function highchartsYAxisLabelFormatter() {
    return formatYAxisCategoryLabel(this.value, widthPx)
  }
}

/**
 * Shift the first 4-digit calendar year inside a forecast/year attribute label so wording stays aligned
 * (e.g. "Downside: 2025E" + Δ=-1 → "Downside: 2024E"). Falls back to null if no year token.
 */
function shiftFirstCalendarYearInLabel(yearLabel, deltaYears) {
  const s = String(yearLabel ?? '')
  if (!/\d{4}/.test(s)) return null
  return s.replace(/\d{4}/, (yyyy) => {
    const y = parseInt(yyyy, 10)
    return Number.isNaN(y) ? yyyy : String(y + deltaYears)
  })
}

/** Legend "prior YE AUM" range: preserve scenario/suffix from filters; numeric fallback when labels lack years. */
function buildLegendPriorYearsLabel(isSingleYear, firstYearAttr, secondYearAttr, priorNumFirst, priorNumSecond) {
  const fallbackSingle = priorNumFirst != null ? String(priorNumFirst) : null
  const fallbackRange =
    priorNumFirst != null && priorNumSecond != null ? `${priorNumFirst}-${priorNumSecond}` : null

  if (isSingleYear) {
    return shiftFirstCalendarYearInLabel(firstYearAttr, -1) ?? fallbackSingle ?? 'prior year'
  }
  const a = shiftFirstCalendarYearInLabel(firstYearAttr, -1)
  const b = shiftFirstCalendarYearInLabel(secondYearAttr, -1)
  if (a && b) {
    const useSpacedSeparator = /[A-Za-z:]/.test(a) || /[A-Za-z:]/.test(b)
    return useSpacedSeparator ? `${a} – ${b}` : `${a}-${b}`
  }
  return fallbackRange ?? 'prior years'
}

export function formatWithCommaSeparator(num) {
  const isNegative = num < 0
  const absNum = Math.abs(num)
  const integerPart = String(Math.floor(absNum))
  let formattedIntegerPart = ''
  let count = 0

  for (let i = integerPart.length - 1; i >= 0; i--) {
    formattedIntegerPart = integerPart[i] + formattedIntegerPart
    count++
    if (count === 3 && i > 0) {
      formattedIntegerPart = ',' + formattedIntegerPart
      count = 0
    }
  }

  return isNegative ? `-${formattedIntegerPart}` : formattedIntegerPart
}

export function computeHeatmapLayoutMetrics(xCategoryCount, yCategoryCount) {
  const hasManyXCategories = xCategoryCount > X_AXIS_MANY_CATEGORIES_THRESHOLD
  const minWidthForColumnSlots =
    HEATMAP_MARGIN_LEFT + HEATMAP_MARGIN_RIGHT + xCategoryCount * HEATMAP_MIN_COLUMN_SLOT_PX
  const heatmapChartWidthPx = Math.max(HEATMAP_CHART_WIDTH, minWidthForColumnSlots)
  const xLabelBoxPx = hasManyXCategories
    ? getResponsiveXLabelWidth(xCategoryCount, true, heatmapChartWidthPx)
    : getResponsiveXLabelWidth(xCategoryCount, false, heatmapChartWidthPx)
  const TOP_HEADER_BLOCK_PX = 128
  const twoLineHeaderReservePx = Math.ceil(
    X_AXIS_LABEL_FONT_PX * X_AXIS_LABEL_LINE_HEIGHT_EM * X_AXIS_LABEL_DISPLAY_LINES + 16
  )
  const tiltedTopReservePx = hasManyXCategories
    ? Math.max(X_AXIS_TILTED_TOP_CLEARANCE_PX, getTiltedXLabelsVerticalReservePx(xLabelBoxPx))
    : 0
  const chartMarginTop = hasManyXCategories
    ? TOP_HEADER_BLOCK_PX + tiltedTopReservePx
    : TOP_HEADER_BLOCK_PX + twoLineHeaderReservePx
  const chartMarginBottom = hasManyXCategories ? 56 : 64
  const chartHeight = yCategoryCount * HEATMAP_ROW_HEIGHT_PX + chartMarginTop + chartMarginBottom

  return {
    hasManyXCategories,
    heatmapChartWidthPx,
    xLabelBoxPx,
    chartMarginTop,
    chartMarginBottom,
    chartHeight,
  }
}

const toNumYear = (y) => (typeof y === 'number' && !Number.isNaN(y)) ? y : parseInt(String(y).match(/\d{4}/)?.[0], 10)

export function resolveYearSelectionContext(selectedAttributeNamesByDimension, chartData) {
  const selectedYears = resolveSelectedYears(selectedAttributeNamesByDimension)
  const hasValidYearSelection = selectedYears && selectedYears.length >= 1 && selectedYears.length <= 2

  let FirstYear
  let SecondYear
  if (chartData !== null && chartData !== undefined && hasValidYearSelection) {
    const years = [...selectedYears].sort((a, b) => toNumYear(a) - toNumYear(b))
    FirstYear = years[0]
    SecondYear = years.length === 2 ? years[1] : null
  }

  const numFirst = FirstYear != null ? toNumYear(FirstYear) : NaN
  const numSecond = SecondYear != null ? toNumYear(SecondYear) : NaN
  const isSameStartEndYear =
    SecondYear != null &&
    !Number.isNaN(numFirst) &&
    !Number.isNaN(numSecond) &&
    numFirst === numSecond
  const yearsLabel =
    FirstYear == null
      ? ''
      : SecondYear == null
        ? String(FirstYear)
        : isSameStartEndYear
          ? String(FirstYear)
          : `${FirstYear} - ${SecondYear}`
  const endYear = SecondYear != null ? SecondYear : FirstYear
  const isSingleYear = (SecondYear == null && FirstYear != null) || isSameStartEndYear

  return {
    FirstYear,
    SecondYear,
    hasValidYearSelection,
    yearsLabel,
    endYear,
    isSingleYear,
    numFirst,
    numSecond,
  }
}

export function HeatmapPreviewPlaceholder({ chart }) {
  return (
    <div style={{ position: 'relative' }}>
      <img
        style={{ opacity: 0.3, width: '100%' }}
        src={chart?.preview || (chart && `data:${chart?.thumbnailMimetype};base64,${chart?.thumbnail}`)}
        alt={chart?.title || ''}
      />
      <Badge
        style={{
          fontFamily: 'McKinsey Sans Regular',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '120px',
          height: '40px',
          fontSize: '16px',
          transform: 'translate(-50%, -50%) rotate(0deg)',
          background: '#B3B3B3',
          color: '#333',
        }}
      >
        PREVIEW
      </Badge>
    </div>
  )
}

export function resolveSelectedYears(selectedAttributeNamesByDimension) {
  const names = selectedAttributeNamesByDimension ?? {}
  return (
    names[DimensionNames.YearID] ??
    names.Years ??
    (() => {
      const yearKey = Object.keys(names).find((key) => String(key).toLowerCase().includes('year'))
      return yearKey ? names[yearKey] : null
    })()
  )
}

export function resolveGeographySubtitleLabel(selectedAttributeNamesByDimension) {
  const names = selectedAttributeNamesByDimension ?? {}
  return (
    names[DimensionNames.CountryID]?.[0] ??
    names.Geographies?.[0] ??
    names.Geography?.[0] ??
    (() => {
      const geographKey = Object.keys(names).find((key) => String(key).toLowerCase().includes('geograph'))
      return geographKey ? names[geographKey]?.[0] : ''
    })() ??
    ''
  )
}

export function resolveClientSegmentSubtitleLabel(selectedAttributeNamesByDimension) {
  const names = selectedAttributeNamesByDimension ?? {}
  return (
    names.SegmentID?.[0] ??
    names['Client Segments']?.[0] ??
    names.ClientSegmentID?.[0] ??
    (() => {
      const clientSegmentKey = Object.keys(names).find((key) => {
        const lower = String(key).toLowerCase()
        return lower.includes('segmentid') || lower.includes('clientsegments')
      })
      return clientSegmentKey ? names[clientSegmentKey]?.[0] : ''
    })() ??
    ''
  )
}

const NetFlowsHeatmapChart = ({
  chart,
  chartData,
  chartRef,
  selectedAttributeNamesByDimension,
  rowAxisLabel,
  resolveFilterSubtitleLabel,
  accessibilityPointDescription,
}) => {
  const [chartOptions, setChartOptions] = useState(null)
  const {
    FirstYear,
    SecondYear,
    hasValidYearSelection,
    yearsLabel,
    endYear,
    isSingleYear,
    numFirst,
    numSecond,
  } = resolveYearSelectionContext(selectedAttributeNamesByDimension, chartData)
  const priorFirst = !Number.isNaN(numFirst) ? numFirst - 1 : null
  const priorSecond = !Number.isNaN(numSecond) ? numSecond - 1 : null
  const filterSubtitleLabel = resolveFilterSubtitleLabel(selectedAttributeNamesByDimension)
  const currencyLabel = selectedAttributeNamesByDimension?.[DimensionNames.ConversionID]?.[0] ?? selectedAttributeNamesByDimension?.['Currency']?.[0] ?? ''
  const headerSubtitleParts = [filterSubtitleLabel, currencyLabel].filter(Boolean)
  const headerSubtitle = headerSubtitleParts.length > 0
    ? `${filterSubtitleLabel}, ${currencyLabel}`
    : ''
  // Header: "Cumulative Net Flow Effects in Years 2020-2024 and AuM at YE 2024" or single year variant
  const headerTitle =
    endYear != null
      ? isSingleYear
        ? `Net Flows in Year ${FirstYear} and AUM at YE ${endYear}`
        : `Cumulative Net Flows in Years ${FirstYear}-${SecondYear} and AUM at YE ${endYear}`
      : ''
  const priorYearsLabel = buildLegendPriorYearsLabel(
    isSingleYear,
    FirstYear,
    SecondYear,
    priorFirst,
    priorSecond
  )
  const legendTitleText =
    endYear != null
      ? isSingleYear
        ? `Annual Net Flows in Year ${FirstYear} as % of YE AUM in ${priorYearsLabel}`
        : `Average Annual Net Flows in Years ${FirstYear}-${SecondYear} as % of Average YE AUM in ${priorYearsLabel}`
      : 'Average Annual Net Flows as % of Average YE AUM'

  useEffect(() => {
    if (!chartData || !hasValidYearSelection) {
      setChartOptions(null)
      return
    }
    const baseYCategories = chartData.yAttributes?.map((item) => item.name) ?? []
    const baseXCategories = chartData.xAttributes?.map((item) => item.name) ?? []
    const numBaseRows = baseYCategories.length
    const numBaseCols = baseXCategories.length
    const totals = chartData.totals ?? {}
    const auMColumnTotal = totals.auMColumnTotal ?? chartData.auMColumnTotal ?? []
    const netFlowsColumnTotals = totals.netFlowsColumnTotals ?? chartData.netFlowsColumnTotals ?? []
    const auMRowTotal = totals.auMRowTotal ?? chartData.auMRowTotal ?? []
    const netFlowsRowTotals = totals.netFlowsRowTotals ?? chartData.netFlowsRowTotals ?? []
    const netFlowsGrandTotal = totals.netFlowsGrandTotal ?? chartData.netFlowsGrandTotal
    const netFlowsGrandTotalPercentage =
      totals.netFlowsGrandTotalPercentage ?? chartData.netFlowsGrandTotalPercentage
    const auMGrandTotal = totals.auMGrandTotal ?? chartData.auMGrandTotal

    const xColLabelTotalNetFlows = `Total Net Flows (${yearsLabel})`
    const xColLabelTotalAuM = `Total AUM (${endYear})`
    // Add 2 rows: Total Net Flows first, then Total AuM (row totals)
    const yCategories = [...baseYCategories, xColLabelTotalNetFlows, xColLabelTotalAuM]
    // Add 2 columns: Total Net Flows first, then Total AuM (column totals)
    const xCategories = [...baseXCategories, xColLabelTotalNetFlows, xColLabelTotalAuM]

    const totalRowNetFlows = numBaseRows
    const totalRowAuM = numBaseRows + 1
    const totalColNetFlows = numBaseCols
    const totalColAuM = numBaseCols + 1

    // Boundary between Total Net Flows and Total AuM columns/rows (cell centers are integer indices)
    const totalBlockEdgeX = totalColNetFlows + 0.5
    const totalBlockEdgeY = totalRowNetFlows + 0.5

    const {
      hasManyXCategories,
      heatmapChartWidthPx,
      xLabelBoxPx,
      chartMarginTop,
      chartMarginBottom,
      chartHeight,
    } = computeHeatmapLayoutMetrics(xCategories.length, yCategories.length)
    const xAxisLabelsAlign = 'center'
    const xAxisLabelsTextAlignCss = 'center'

    const mainData = (chartData.dataPoints ?? []).map((item) => {
      const value = getValue(item.value)
      const numValue = value != null && !Number.isNaN(value) ? Number(value) : value
      const initialVal = getValue(item.initialValue)
      const yName = baseYCategories[item.yCoordinate] || ''
      const xName = baseXCategories[item.xCoordinate] || ''
      const displayValue = value == null || Number.isNaN(value) ? 'n/a' : `${Number(value).toFixed(1)}%`
      const customText = initialVal != null && !Number.isNaN(initialVal) ? formatWithCommaSeparator(initialVal) : (item.initialValue ?? 'n/a')
      const fontColor = getHeatmapFontColor(numValue)
      const pointColor = getHeatmapCellColor(numValue)
      return {
        x: item.xCoordinate,
        y: item.yCoordinate,
        value: numValue,
        color: pointColor,
        fillColor: fontColor,
        customText,
        hoverText: item.hoverText || `<b>${displayValue}</b><br />${rowAxisLabel}: ${yName}<br />Asset class: ${xName}${yearsLabel ? `<br />Years: ${yearsLabel}` : ''}`,
      }
    })

    const columnTotalAuMPoints = auMColumnTotal.map((val, y) => {
      const displayAuM = val != null && !Number.isNaN(val) ? formatWithCommaSeparator(val) : 'n/a'
      return {
        x: totalColAuM,
        y,
        value: null,
        color: 'rgb(255, 255, 255)',
        fillColor: '#000000',
        customText: displayAuM,
        hoverText: `AUM total: <b>${displayAuM}</b><br />${rowAxisLabel}: ${baseYCategories[y] || ''}`,
      }
    })
    const columnTotalNetFlowsPoints = netFlowsColumnTotals.map((elt, y) => {
      const rawVal = elt && typeof elt === 'object' && 'total' in elt ? Number(elt.total) : null
      const percentage = elt?.percentage != null && !Number.isNaN(Number(elt.percentage)) ? Number(elt.percentage) : null
      const displayValue = rawVal != null && !Number.isNaN(rawVal) ? formatWithCommaSeparator(rawVal) : 'n/a'
      const hoverPct = percentage != null && !Number.isNaN(percentage) ? `${Number(percentage).toFixed(1)}%` : 'n/a'
      return {
        x: totalColNetFlows,
        y,
        value: rawVal,
        color: getHeatmapCellColor(percentage),
        fillColor: getHeatmapFontColor(percentage),
        customText: displayValue,
        hoverText: `<b>${hoverPct}</b><br />${rowAxisLabel}: ${baseYCategories[y] || ''}`,
      }
    })
    const rowTotalAuMPoints = auMRowTotal.map((val, x) => {
      const displayAuM = val != null && !Number.isNaN(val) ? formatWithCommaSeparator(val) : 'n/a'
      return {
        x,
        y: totalRowAuM,
        value: null,
        color: 'rgb(255, 255, 255)',
        fillColor: '#000000',
        customText: displayAuM,
        hoverText: `AUM total: <b>${displayAuM}</b><br />Asset class: ${baseXCategories[x] || ''}`,
      }
    })
    const rowTotalNetFlowsPoints = netFlowsRowTotals.map((elt, x) => {
      const rawVal = elt && typeof elt === 'object' && 'total' in elt ? Number(elt.total) : null
      const percentage = elt?.percentage != null && !Number.isNaN(Number(elt.percentage)) ? Number(elt.percentage) : null
      const displayValue = rawVal != null && !Number.isNaN(rawVal) ? formatWithCommaSeparator(rawVal) : 'n/a'
      const hoverPct = percentage != null && !Number.isNaN(percentage) ? `${Number(percentage).toFixed(1)}%` : 'n/a'
      return {
        x,
        y: totalRowNetFlows,
        value: rawVal,
        color: getHeatmapCellColor(percentage),
        fillColor: getHeatmapFontColor(percentage),
        customText: displayValue,
        hoverText: `<b>${hoverPct}</b><br />Asset class: ${baseXCategories[x] || ''} <br /> Total Net Flows (${yearsLabel})`,
      }
    })

    const grandTotalAuMPoint = (() => {
      const val = auMGrandTotal != null && !Number.isNaN(Number(auMGrandTotal)) ? Number(auMGrandTotal) : null
      const displayAuM = val != null ? formatWithCommaSeparator(val) : 'n/a'
      return {
        x: totalColAuM,
        y: totalRowAuM,
        value: null,
        color: 'rgb(255, 255, 255)',
        fillColor: '#000000',
        customText: displayAuM,
        hoverText: `AUM Grand Total (${endYear}): <b>${displayAuM}</b>`,
      }
    })()
    const grandTotalNetFlowsPoint = (() => {
      const rawVal = netFlowsGrandTotal != null && !Number.isNaN(Number(netFlowsGrandTotal)) ? Number(netFlowsGrandTotal) : null
      const percentage = netFlowsGrandTotalPercentage != null && !Number.isNaN(Number(netFlowsGrandTotalPercentage))
        ? Number(netFlowsGrandTotalPercentage)
        : null
      const displayValue = rawVal != null ? formatWithCommaSeparator(rawVal) : 'n/a'
      const hoverPct = percentage != null ? `${Number(percentage).toFixed(1)}%` : 'n/a'
      return {
        x: totalColNetFlows,
        y: totalRowNetFlows,
        value: rawVal,
        color: getHeatmapCellColor(percentage),
        fillColor: getHeatmapFontColor(percentage),
        customText: displayValue,
        hoverText: `<b>${hoverPct}</br> Total Net Flows (${yearsLabel})`,
      }
    })()

    const allData = [
      ...mainData,
      ...columnTotalAuMPoints,
      ...columnTotalNetFlowsPoints,
      ...rowTotalAuMPoints,
      ...rowTotalNetFlowsPoints,
      grandTotalNetFlowsPoint,
      grandTotalAuMPoint,
    ]

    setChartOptions({
      offlineExporting: {
        enabled: true,
      },
      chart: {
        type: 'heatmap',
        width: heatmapChartWidthPx,
        height: chartHeight,
        marginLeft: HEATMAP_MARGIN_LEFT,
        marginRight: HEATMAP_MARGIN_RIGHT,
        marginTop: chartMarginTop,
        marginBottom: chartMarginBottom,
        spacingTop: hasManyXCategories ? 6 : 0,
        spacingBottom: 16,
        plotBorderWidth: 1,
        plotBorderColor: CHART_TABLE_OUTER_BORDER_COLOR,
        plotBackgroundColor: '#ffffff',
        exportEnabled: true,
      },
      title: {
        text: headerTitle || chart.title + (yearsLabel ? `, ${yearsLabel}` : ''),
        align: 'left',
        style: {
          fontFamily: 'McKinsey Sans Regular',
          color: '#000000',
          textOutline: '0px contrast',
          fontSize: '20px',
          fontWeight: 'normal',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
      subtitle: {
        text: headerSubtitle || undefined,
        align: 'left',
        style: {
          fontFamily: 'McKinsey Sans Regular',
          color: 'rgb(117, 117, 117)',
          textOutline: '0px contrast',
          fontSize: '14px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
      yAxis: {
        categories: yCategories,
        reversed: true,
        title: {
          text: null,
        },
        lineWidth: 0,
        gridLineWidth: 0,
        tickLength: 0,
        minorGridLineWidth: 0,
        plotLines: [
          {
            value: totalBlockEdgeY,
            color: TOTAL_BLOCK_SEPARATOR_COLOR,
            width: 1,
            zIndex: 5,
            dashStyle: 'Solid',
          },
        ],
        labels: {
          useHTML: true,
          formatter: createYAxisLabelsFormatter(Y_AXIS_LABEL_WIDTH_PX),
          style: {
            fontFamily: 'McKinsey Sans Regular',
            width: `${Y_AXIS_LABEL_WIDTH_PX}px`,
            textAlign: 'left',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
        },
      },
      xAxis: {
        opposite: true,
        offset: hasManyXCategories ? 6 : 0,
        categories: xCategories,
        lineWidth: 0,
        gridLineWidth: 0,
        tickLength: 0,
        tickWidth: 0,
        minorGridLineWidth: 0,
        plotLines: [
          {
            value: totalBlockEdgeX,
            color: TOTAL_BLOCK_SEPARATOR_COLOR,
            width: 1,
            zIndex: 5,
            dashStyle: 'Solid',
          },
        ],
        labels: {
          align: xAxisLabelsAlign,
          verticalAlign: 'middle',
          rotation: hasManyXCategories ? X_AXIS_TILTED_LABEL_ROTATION_DEG : 0,
          ...(hasManyXCategories ? { distance: X_AXIS_TILTED_LABEL_DISTANCE_PX } : {}),
          x: 0,
          // Top x-axis (side 0): negative `y` moves labels up; too much creates a gap above the grid.
          y: hasManyXCategories ? -18 : 0,
          useHTML: true,
          formatter: createXAxisLabelsFormatter(
            xLabelBoxPx,
            xAxisLabelsTextAlignCss,
            hasManyXCategories
          ),
          style: {
            fontFamily: 'McKinsey Sans Regular',
            width: `${xLabelBoxPx}px`,
            textAlign: xAxisLabelsTextAlignCss,
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
        },
      },
      accessibility: {
        point: {
          descriptionFormat:
            '{(add index 1)}. ' +
            accessibilityPointDescription,
        },
        enabled: false
      },
      colorAxis: {
        min: -10,
        max: 8,
        title: {
          text: 'Net flow %',
          style: {
            fontFamily: 'McKinsey Sans Regular',
          },
        },
        dataClasses: [
          { from: 6, color: HEATMAP_COLORS.aboveSix.color, name: '>6%' },
          { from: 4, to: 6, color: HEATMAP_COLORS.fourToSix.color, name: '4–6%' },
          { from: 2, to: 4, color: HEATMAP_COLORS.twoToFour.color, name: '2–4%' },
          { from: 0, to: 2, color: HEATMAP_COLORS.zeroToTwo.color, name: '0–2%' },
          { from: -100, to: -0.0001, color: HEATMAP_COLORS.belowZero.color, name: '<0%' },
          { from: -0.0001, to: 0, color: HEATMAP_COLORS.na.color, name: 'n/a' },
        ],
      },
      legend: {
        layout: 'horizontal',
        align: 'right',
        verticalAlign: 'top',
        itemStyle: {
          fontFamily: 'McKinsey Sans Regular',
        },
        title: {
          align: 'right',
          text: legendTitleText,
          style: {
            fontFamily: 'McKinsey Sans Regular',
            align: 'right',
            fontWeight: 'light',
            fontSize: '14px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          },
        },
      },
      tooltip: {
        useHTML: true,
        outside: true,
        headerFormat: '',
        pointFormat: `<div style="font-family: 'McKinsey Sans Regular'; width: 250px; line-height: 1.5; white-space: normal;">{point.hoverText}</div>`,
        style: {
          fontFamily: 'McKinsey Sans Regular',
          lineHeight: '1.5',
        },
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: 'Net flow %',
          borderWidth: 1,
          borderColor: '#fff',
          data: allData,
          dataLabels: {
            enabled: true,
            format: '<span style="width : 250px; fill: {point.fillColor}; font-family: \'McKinsey Sans Regular\';">{point.customText}</span>',
            style: {
              textOutline: '0px contrast',
              fontSize: '12px',
              fontWeight: '100',
              fontFamily: 'McKinsey Sans Regular',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            },
          },
        },
      ],
      exporting: {
        enabled: false,
        fallbackToExportServer: false,
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
          },
        ],
      },
    })
  }, [chartData, chart, selectedAttributeNamesByDimension, hasValidYearSelection, yearsLabel, legendTitleText, headerTitle, headerSubtitle, rowAxisLabel, accessibilityPointDescription])

  return (
    <div className="highchart custom-overflow">
      {
        chartData && chartOptions && hasValidYearSelection ?
          <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} /> :
          <HeatmapPreviewPlaceholder chart={chart} />
      }
    </div>
  )
}

export default NetFlowsHeatmapChart
